import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Mic, Users, ShieldCheck } from "lucide-react";
import { MaskText, GoldButton, Reveal, SectionLabel } from "../components/elysium/primitives";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706601822656-08a1a6ce7c0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const steps = [
  { icon: KeyRound, title: "Create a Soul-Key", body: "Start a private legacy for yourself or someone you love." },
  { icon: Mic, title: "Record their stories", body: "Capture memories, lessons, and wisdom in their own words." },
  { icon: Users, title: "Share with family", body: "Grant loved ones access in the mode you choose — archive, guided, or open." },
];

const modes = [
  { name: "Archive", desc: "Browse the recorded stories. No conversation." },
  { name: "Guided", desc: "The AI answers only from recorded stories — never invents." },
  { name: "Open", desc: "The AI may gently generalize from tone & values, always clearly flagged." },
];

export const Landing = () => {
  return (
    <div className="relative min-h-screen bg-ely-bg">
      <div className="grain" aria-hidden="true" />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-ely-surface/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-2">
            <span className="font-serif text-2xl text-ely-text">Soul-Key</span>
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ely-primary" />
          </div>
          <div className="flex items-center gap-5">
            <Link to="/login" data-testid="nav-login" className="font-sans text-sm text-ely-muted transition-colors hover:text-ely-text">Log In</Link>
            <Link to="/register" data-testid="nav-cta" className="rounded-full border border-ely-border px-5 py-2 font-sans text-sm text-ely-text transition-all hover:border-ely-primary hover:text-ely-primary">Begin Your Legacy</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-24 pt-40 md:px-10 md:pt-52" data-testid="hero">
        <div className="pointer-events-none absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ely-bg via-ely-bg/85 to-ely-bg/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-ely-bg to-transparent" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-ely-primary/60" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-ely-muted">AI Legacy Platform</span>
          </motion.div>
          <h1 className="font-serif text-5xl font-light leading-[0.95] tracking-tight text-ely-text md:text-7xl">
            <MaskText delay={0.35} lines={["The conversations", "that never have", "to end."]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }} className="mt-8 max-w-2xl font-sans text-lg font-light leading-relaxed text-ely-muted">
            Preserve the stories, voice, and wisdom of the people you love — and let family continue the conversation through an AI built only from what was actually recorded.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 1 }} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/register"><GoldButton data-testid="hero-cta">Begin Building a Legacy</GoldButton></Link>
            <Link to="/login"><GoldButton variant="outline" data-testid="hero-login">I already have an account</GoldButton></Link>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-ely-border/40 px-6 py-24 md:px-10" data-testid="how-section">
        <div className="mx-auto max-w-5xl">
          <SectionLabel index="01">How it works</SectionLabel>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="rounded-xl border border-ely-border/60 bg-ely-surface/40 p-7">
                  <s.icon size={22} strokeWidth={1.4} className="text-ely-primary" />
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-wider text-ely-muted">Step {i + 1}</p>
                  <h3 className="mt-2 font-serif text-2xl font-light text-ely-text">{s.title}</h3>
                  <p className="mt-3 font-sans text-sm font-light leading-relaxed text-ely-muted">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Access modes */}
      <section className="border-t border-ely-border/40 px-6 py-24 md:px-10" data-testid="modes-section">
        <div className="mx-auto max-w-5xl">
          <SectionLabel index="02">You decide who, and how</SectionLabel>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ely-text md:text-5xl">Three ways to share a legacy.</h2>
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-ely-border/60 md:grid-cols-3">
            {modes.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1} className="bg-ely-surface/40 p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ely-primary">{m.name}</span>
                <p className="mt-4 font-serif text-xl font-light leading-snug text-ely-text">{m.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-ely-border/40 px-6 py-24 md:px-10" data-testid="trust-section">
        <div className="mx-auto max-w-3xl text-center">
          <ShieldCheck size={26} className="mx-auto text-ely-primary" />
          <h2 className="mt-8 font-serif text-3xl font-light leading-snug tracking-tight text-ely-text md:text-4xl">
            An AI reflection — never a pretend person.
          </h2>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-ely-muted">
            Every answer is grounded in real recorded stories and shows its sources. It refers to your loved one in their own words, and never claims to be them.
          </p>
          <Link to="/register" className="mt-10 inline-block"><GoldButton data-testid="trust-cta">Start Your Legacy</GoldButton></Link>
        </div>
      </section>

      <footer className="border-t border-ely-border/40 px-6 py-10 md:px-10">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 md:flex-row">
          <span className="font-serif text-xl text-ely-text">Soul-Key</span>
          <p className="font-sans text-xs font-light text-ely-muted">When memories become conversations, love never stops teaching.</p>
          <span className="font-mono text-[10px] uppercase tracking-wider text-ely-muted/60">© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
